import {
  CARD,
  CARD_EXPORT_HEIGHT,
  CARD_EXPORT_WIDTH,
  CARD_INK,
  CARD_MUTED,
  px,
} from './cardLayout';
import { qrMatrix, siteEntryUrl } from './qr';
import { LEVEL_IDENTITY } from './emblem';
import { buildShareUrl } from './scoring';
import type { AssessmentResult, Mode, UiCopy } from './types';

export type SocialPlatform =
  | 'x'
  | 'instagram'
  | 'xiaohongshu'
  | 'weibo';

const SANS = '"Inter", "Helvetica Neue", "PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC", Arial, sans-serif';
const DISPLAY = 'Georgia, "Songti SC", "Noto Serif CJK SC", "Times New Roman", serif';
const MONO = 'ui-monospace, "SF Mono", Menlo, "Noto Sans Mono CJK SC", monospace';

function font(weight: number, sizeUnits: number, family: string): string {
  return `${weight} ${px(sizeUnits).toFixed(1)}px ${family}`;
}

function tracked(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  spacing: number,
  align: 'left' | 'center' | 'right' = 'left',
): void {
  const characters = [...text];
  const width = characters.reduce(
    (total, character) => total + context.measureText(character).width + spacing,
    -spacing,
  );
  let cursor = align === 'right' ? x - width : align === 'center' ? x - width / 2 : x;
  for (const character of characters) {
    context.fillText(character, cursor, y);
    cursor += context.measureText(character).width + spacing;
  }
}

function fitOneLine(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  weight: number,
  sizeUnits: number,
  family: string,
): void {
  let size = sizeUnits;
  context.font = font(weight, size, family);
  while (context.measureText(text).width > maxWidth && size > sizeUnits * 0.62) {
    size -= sizeUnits * 0.04;
    context.font = font(weight, size, family);
  }
}

function wrapLines(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const spaced = text.includes(' ');
  const tokens = spaced ? text.split(' ') : [...text];
  const lines: string[] = [];
  let line = '';

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index] ?? '';
    const candidate = line ? `${line}${spaced ? ' ' : ''}${token}` : token;
    if (context.measureText(candidate).width <= maxWidth || !line) {
      line = candidate;
      continue;
    }
    lines.push(line);
    line = token;
    if (lines.length === maxLines - 1) {
      let rest = [token, ...tokens.slice(index + 1)].join(spaced ? ' ' : '');
      while (rest.length > 1 && context.measureText(`${rest}…`).width > maxWidth) {
        rest = rest.slice(0, -1).trimEnd();
      }
      lines.push(rest === [token, ...tokens.slice(index + 1)].join(spaced ? ' ' : '') ? rest : `${rest}…`);
      return lines.slice(0, maxLines);
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function drawQr(context: CanvasRenderingContext2D, payload: string): void {
  const matrix = qrMatrix(payload);
  const box = px(CARD.qr.size);
  const quiet = px(CARD.qr.quiet);
  const x = CARD_EXPORT_WIDTH - px(CARD.padX) - box;
  const y = px(CARD.qr.y);
  const module = (box - quiet * 2) / matrix.size;

  context.save();
  context.fillStyle = '#f4f1ea';
  roundedRect(context, x, y, box, box, px(0.6));
  context.fill();
  context.fillStyle = '#0a0b0f';
  for (let row = 0; row < matrix.size; row += 1) {
    for (let column = 0; column < matrix.size; column += 1) {
      if (!matrix.modules[row]?.[column]) continue;
      context.fillRect(
        x + quiet + column * module,
        y + quiet + row * module,
        Math.ceil(module),
        Math.ceil(module),
      );
    }
  }
  context.restore();
}

function loadCardBase(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('card_base_failed'));
    image.src = source;
  });
}

const LEVEL_WORDS = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'];

function bilingualLabel(primary: string, english: string): string {
  return primary.trim().toUpperCase() === english ? english : `${primary} / ${english}`;
}

function drawIdentity(
  context: CanvasRenderingContext2D,
  result: { identityLabel: string; identityTitle: string; identityMeta: string },
): void {
  context.save();
  const left = px(CARD.padX);
  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.fillStyle = CARD_MUTED;
  context.font = font(700, CARD.identity.labelSize, MONO);
  tracked(context, result.identityLabel, left, px(CARD.identity.labelY), px(0.1));
  context.fillStyle = CARD_INK;
  fitOneLine(context, result.identityTitle, CARD_EXPORT_WIDTH * 0.7, 720, CARD.identity.titleSize, SANS);
  context.fillText(result.identityTitle, left, px(CARD.identity.titleY));
  context.fillStyle = CARD_MUTED;
  fitOneLine(context, result.identityMeta, CARD_EXPORT_WIDTH * 0.7, 500, CARD.identity.metaSize, DISPLAY);
  context.fillText(result.identityMeta, left, px(CARD.identity.metaY));
  context.restore();
}

function drawLevel(
  context: CanvasRenderingContext2D,
  result: { label: string; number: number; accent: string },
): void {
  context.save();
  context.textBaseline = 'alphabetic';
  context.fillStyle = CARD_INK;
  context.font = font(760, CARD.level.labelSize, SANS);
  const labelWidth = context.measureText(result.label).width;
  context.font = font(820, CARD.level.numberSize, SANS);
  const numberText = String(result.number);
  const numberWidth = context.measureText(numberText).width;
  const gap = px(2.2);
  const start = (CARD_EXPORT_WIDTH - labelWidth - gap - numberWidth) / 2;
  context.font = font(760, CARD.level.labelSize, SANS);
  context.fillText(result.label, start, px(CARD.level.y));
  context.font = font(820, CARD.level.numberSize, SANS);
  context.fillText(numberText, start + labelWidth + gap, px(CARD.level.y));

  context.fillStyle = result.accent;
  context.font = font(700, CARD.level.englishSize, MONO);
  tracked(
    context,
    `LEVEL ${LEVEL_WORDS[result.number]}`,
    CARD_EXPORT_WIDTH / 2,
    px(CARD.level.englishY),
    px(0.25),
    'center',
  );
  context.restore();
}

export async function createShareCard(
  result: AssessmentResult,
  ui: UiCopy,
  mode: Mode,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_EXPORT_WIDTH;
  canvas.height = CARD_EXPORT_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas_unavailable');

  const levelIdentity = LEVEL_IDENTITY[result.levelId];
  const type = ui.types[result.typeId];
  const left = px(CARD.padX);
  const right = CARD_EXPORT_WIDTH - px(CARD.padX);
  // The generated raster artwork is the real card surface for both the DOM
  // preview and the exported PNG. Dynamic result data is drawn above it.
  const cardBase = await loadCardBase(levelIdentity.artwork);
  context.drawImage(cardBase, 0, 0, CARD_EXPORT_WIDTH, CARD_EXPORT_HEIGHT);

  // Top rail.
  context.fillStyle = CARD_INK;
  context.font = font(700, CARD.rail.size, MONO);
  tracked(
    context,
    `— ${ui.share.boundaryTitle} —`,
    CARD_EXPORT_WIDTH / 2,
    px(CARD.rail.y),
    px(0.12),
    'center',
  );

  drawIdentity(context, {
    identityLabel: bilingualLabel(ui.share.roleLabel, 'IDENTITY'),
    identityTitle: type.title,
    identityMeta: `${ui.modes[mode].label} · ${ui.levels[result.levelId].title}`,
  });
  drawLevel(context, {
    label: ui.share.levelLabel,
    number: levelIdentity.number,
    accent: levelIdentity.accent,
  });

  context.save();
  context.fillStyle = CARD_MUTED;
  context.font = font(480, CARD.desc.size, SANS);
  context.textAlign = 'center';
  const descriptionLines = wrapLines(
    context,
    ui.levels[result.levelId].description,
    CARD_EXPORT_WIDTH * 0.64,
    CARD.desc.lines,
  );
  descriptionLines.forEach((line, index) => {
    context.fillText(line, CARD_EXPORT_WIDTH / 2, px(CARD.desc.y + index * CARD.desc.leading));
  });
  context.restore();

  // Score band.
  context.fillStyle = CARD_INK;
  context.font = font(800, CARD.score.size, SANS);
  const scoreText = String(result.total);
  const numeralWidth = context.measureText(scoreText).width;
  const scoreGap = px(1.4);
  context.font = font(600, CARD.score.unitSize, SANS);
  const unitWidth = context.measureText('/100').width;
  const scoreStart = (CARD_EXPORT_WIDTH - numeralWidth - scoreGap - unitWidth) / 2;
  context.font = font(800, CARD.score.size, SANS);
  context.fillText(scoreText, scoreStart, px(CARD.score.y));
  context.fillStyle = CARD_MUTED;
  context.font = font(600, CARD.score.unitSize, SANS);
  context.fillText('/100', scoreStart + numeralWidth + scoreGap, px(CARD.score.y));

  // Meta strip.
  context.fillStyle = CARD_MUTED;
  context.font = font(700, CARD.meta.labelSize, MONO);
  tracked(
    context,
    bilingualLabel(ui.share.testTypeLabel, 'TEST TYPE'),
    left,
    px(CARD.meta.labelY),
    px(0.1),
  );
  context.fillStyle = CARD_INK;
  fitOneLine(context, ui.share.cardTitle, px(48), 650, CARD.meta.valueSize, SANS);
  context.fillText(ui.share.cardTitle, left, px(CARD.meta.valueY));

  const refX = px(CARD.meta.refX);
  context.fillStyle = CARD_MUTED;
  context.font = font(700, CARD.meta.labelSize, MONO);
  tracked(
    context,
    bilingualLabel(ui.share.referenceLabel, 'REF'),
    refX,
    px(CARD.meta.labelY),
    px(0.1),
  );
  context.fillStyle = levelIdentity.accent;
  context.font = font(700, CARD.meta.valueSize, MONO);
  tracked(context, `REF·P${result.percentile}`, refX, px(CARD.meta.valueY), px(0.1));

  drawQr(context, siteEntryUrl(window.location.href));

  context.strokeStyle = 'rgba(244, 241, 234, 0.16)';
  context.lineWidth = 1.5;
  context.beginPath();
  context.moveTo(left, px(CARD.ruleFoot));
  context.lineTo(right - px(CARD.qr.size + 2.8), px(CARD.ruleFoot));
  context.stroke();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('share_card_failed'))),
      'image/png',
    );
  });
}

export function downloadShareCard(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'the-line-card.png';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function shareText(result: AssessmentResult, ui: UiCopy): string {
  return ui.share.text
    .replace('{type}', ui.types[result.typeId].title)
    .replace('{score}', String(result.total));
}

export function buildSocialShareUrl(
  platform: SocialPlatform,
  url: string,
  text: string,
  title: string,
): string {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  const encodedTitle = encodeURIComponent(title);
  const destinations: Record<SocialPlatform, string> = {
    x: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    instagram: `https://www.instagram.com/`,
    xiaohongshu: `https://www.xiaohongshu.com/`,
    weibo: `https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodedText}`,
  };
  return destinations[platform];
}

export function openSocialShare(
  platform: SocialPlatform,
  url: string,
  text: string,
  title: string,
): void {
  window.open(
    buildSocialShareUrl(platform, url, text, title),
    '_blank',
    'noopener,noreferrer,width=720,height=680',
  );
}

export async function nativeShare(
  blob: Blob,
  result: AssessmentResult,
  ui: UiCopy,
  locale: string,
): Promise<boolean> {
  if (!navigator.share) return false;
  const url = buildShareUrl(
    window.location.href,
    result.typeId,
    result.total,
    result.percentile,
    locale,
  );
  const file = new File([blob], 'the-line-card.png', { type: 'image/png' });
  const title = `THE LINE — ${ui.types[result.typeId].title}`;
  const text = shareText(result, ui);

  if (navigator.canShare && !navigator.canShare({ files: [file] })) {
    await navigator.share({ title, text, url });
    return true;
  }
  await navigator.share({ title, text, url, files: [file] });
  return true;
}
